import {ApiUrls} from 'shared/config/model/urls';

import {useDispatch} from 'react-redux';

import {httpIncidentAPI} from 'features/http/api/httpIncidentAPI';
import {httpMonitorAPI} from 'features/http/api/httpMonitorAPI';
import {hasSendableBatchFields} from 'features/http/lib/batchEditFields';
import {
    buildHttpBatchOperationRequest,
    getHttpBatchOperationsDefaults,
} from 'features/http/lib/httpMonitorFormHelper';
import CreateHttpMonitorType from 'features/http/model/HttpMonitor/CreateHttpMonitorType';
import {
    useMonitorBatchOperationsForm,
    IMonitorBatchOperationsFormState,
    isBatchDeleteAction,
    invalidateDomainIncidents,
} from 'entities/monitor';
import {TypedDispatch} from 'shared/model/types';

import {useHttpMonitorSelection} from './useHttpMonitorSelection';

interface IUseHttpMonitorBatchOperationsFormParams {
    actionValue?: string,
    handleClose: () => void,
    onSuccess?: () => void,
}

export const useHttpMonitorBatchOperationsForm = ({
    actionValue,
    handleClose,
    onSuccess,
}: IUseHttpMonitorBatchOperationsFormParams): IMonitorBatchOperationsFormState<CreateHttpMonitorType> => {
    const {selectedIds} = useHttpMonitorSelection();
    const dispatch = useDispatch<TypedDispatch>();

    const invalidate = (action: string | undefined, ids: string[]) => {
        dispatch(httpMonitorAPI.util.invalidateTags([{type: 'HttpMonitor', id: 'LIST'}, 'SummaryHttpMonitors']));

        if (!isBatchDeleteAction(action)) {
            ids.forEach(uuid => {
                dispatch(httpMonitorAPI.util.invalidateTags([{type: 'HttpMonitor', id: uuid}]));
            });
        }

        if (action !== 'tags') {
            dispatch(httpIncidentAPI.util.invalidateTags(['HttpIncident', 'SummaryHttpIncident']));
            invalidateDomainIncidents(dispatch);
        }
    };

    return useMonitorBatchOperationsForm<CreateHttpMonitorType>({
        actionValue,
        handleClose,
        onSuccess,
        selectedIds,
        getDefaultValues: getHttpBatchOperationsDefaults,
        getUrl: (action) => action === 'tags'
            ? ApiUrls.HTTP_MONITORS_BATCH_TAGS
            : ApiUrls.HTTP_MONITORS_BATCH_ACTION(action ?? ''),
        buildRequest: buildHttpBatchOperationRequest,
        invalidate,
        canSubmitEdit: (addedFields, form) => hasSendableBatchFields(addedFields, form.watch('method')),
        onFieldRemoved: (key, form) => {
            if (key === 'method') {
                form.setValue('method', 'HEAD');
            }
        },
    });
};

export default useHttpMonitorBatchOperationsForm;
